<template>
    <div class="details-page-wrapper">
      <b-loading :active="isFetching" :is-full-page="false" />

      <h2 class="title">Transaction Details</h2>
      <h3 class="subtitle">Transaction ID {{ jobDetails.basicInfo.companyJobId }}</h3>
      <h3 class="subtitle">
        Rate: {{ formattedRate }}
      </h3>

      <!--
      Only show the cards once we are done loading the data. This avoid funkyness with other components since we would
      pass in default and cause errors.
       -->
      <template v-if="!isFetching">

        <b-notification v-if="hasError" type="is-danger" has-icon :closable="false">
          Looks like we encountered an error when trying to load the details for this transaction.
          <br/>
          Please try logging out and back it or contact support for further assistance.
          <br/>
          <br/>
          <pre>
            {{ errorDetails }}
          </pre>
        </b-notification>

        <div class="columns" v-if="!hasError">
          <div class="column is-one-third">
            <div class="card">
              <p class="card-header-title">
                Executed Milestones
              </p>
              <div class="card-content">
                <mile-stones :is-job-certified="jobDetails.companyJobCarrierCertifiedAccurateFlag" :company-job-key="jobDetails.companyJobKey" />
              </div>
            </div>
          </div>

        <div class="column">
        <!-- milestones & map -->
        <b-collapse class="card" :open="true">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Route & Travel
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <div class="content">

              <div class="columns content-milestones-and-route">
                <!--
                <div class="column">
                  <mile-stones :company-job-key="jobDetails.companyJobKey" />
                </div>
                -->

                <div class="column">
                  <route-map :job-points="[...jobDetails.origins, ...jobDetails.destinations]" :company-job-key="jobDetails.companyJobKey" />
                </div>
              </div>
            </div>
          </div>
        </b-collapse>

        <!-- Origins -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Origin
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <job-point-info class="job-point" v-for="(point, index) in originsWithCheckinTime" :point="point" :key="index" />
          </div>
        </b-collapse>

        <!-- Destinations -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Destination
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <job-point-info class="job-point" v-for="(point, index) in destinationsWithCheckinTime" :point="point" :key="index" />
          </div>
        </b-collapse>

        <!-- Load details -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Freight
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <div class="content">
              <freight-info :freight="jobDetails.freight" />
            </div>

          </div>
        </b-collapse>

        <!-- Equipment details -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Equipment
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <div class="content">
              <equipment-info :equipment="jobDetails.equipment" />
            </div>

          </div>
        </b-collapse>

        <!-- Invite list -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Invite List
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <div class="content">
              <invite-list :company-job-key="companyJobKey"  />
            </div>

          </div>
        </b-collapse>

        <!-- Document list -->
        <b-collapse :open="false" class="card">
          <div slot="trigger" slot-scope="props" class="card-header">
            <p class="card-header-title">
              Documents
            </p>
            <a class="card-header-icon">
              <font-awesome-icon :icon="props.open ? 'chevron-down' : 'chevron-up'" />
            </a>
          </div>
          <div class="card-content">
            <div class="content">
              <document-library :company-job-key="jobDetails.companyJobKey" :can-delete="false"/>
            </div>

          </div>
        </b-collapse>
        </div>
        </div>
      </template>
    </div>
</template>

<script>
// import { mapState, mapActions, mapGetters } from 'vuex'
import Numeral from 'numeral'
import { mapState, mapActions } from 'vuex'
// this is a custom Polymer component
// eslint-disable-next-line
// import BlockcertsVerifier from '@blockcerts/blockcerts-verifier'

import CompanyJobApi from '@/api-interface/companyjob'
import restoreCompanyJob from '@/helpers/restoreCompanyJob'
import JobPointInfo from '@/components/TransactionDetails/JobPointInformation'
import FreightInfo from '@/components/TransactionDetails/FreightInformation'
import EquipmentInfo from '@/components/TransactionDetails/EquipmentInformation'
import InviteList from '@/components/CompanyJobInviteList'
import DocumentLibrary from '@/components/CompanyDocumentsTable/index'
import RouteMap from '@/components/TransactionDetails/RouteMap'
import MileStones from '@/components/TransactionDetails/MileStones'
export default {
  name: 'Transaction-Details',

  async created() {
    this.companyJobKey = this.$route.params.companyJobKey
    await this._initDetails()
  },

  components: {
    MileStones,
    RouteMap,
    JobPointInfo,
    FreightInfo,
    EquipmentInfo,
    InviteList,
    DocumentLibrary
  },

  computed: {
    formattedRate() {
      return Numeral(this.jobDetails.basicInfo.rate).format('$0,0.00')
    },

    ...mapState('companyJobs', ['executionLogs']),

    currentTransactionExecutionLog() {
      return this.executionLogs[this.companyJobKey] || []
    },

    checkInMilestones() {
      return this.currentTransactionExecutionLog.filter((milestone) =>
        milestone.companyJobExecutionLogEntryKey.endsWith('_check_in')
      )
    },

    originsWithCheckinTime() {
      return this.jobDetails.origins.map((origin) => ({
        ...origin,
        checkInMileStone:
          this.checkInMilestones.find((checkin) => checkin.companyJobPointKey === origin.companyJobPointKey) || {}
      }))
    },

    destinationsWithCheckinTime() {
      return this.jobDetails.destinations.map((destination) => ({
        ...destination,
        checkInMileStone:
          this.checkInMilestones.find((checkin) => checkin.companyJobPointKey === destination.companyJobPointKey) || {}
      }))
    }
  },

  data() {
    return {
      companyJobKey: null,
      hasError: false,
      errorDetails: null,
      isFetching: false,
      jobDetails: {
        companyJobStatus: 'draft',
        companyJobKey: 0,
        createdByAccountKey: null,
        basicInfo: {
          companyJobId: null,
          rate: null,
          milestones: {},
          documents: []
        },
        origins: [],
        destinations: [],
        freight: [],
        equipment: {},
        companyJobPointFreightMapping: []
      }
    }
  },

  methods: {
    ...mapActions('companyJobs', ['fetchExecutionLogByKey']),
    /**
     * Fetch all the individual bits
     *
     * @private
     */
    async _initDetails() {
      this.isFetching = true
      this.hasError = false

      if (!this.currentTransactionExecutionLog.length) {
        this.fetchExecutionLogByKey(this.companyJobKey)
      }

      try {
        const rawData = (await CompanyJobApi.getByKey(this.companyJobKey)).data
        await this.restoreSavedData(rawData)
      } catch (error) {
        this.hasError = true
        this.errorDetails = error.message
      } finally {
        this.isFetching = false
      }
    },

    /**
     * Takes the "raw" CompanyJob object from the server and re-maps everything in to sensible bits that can be used by the UI.
     * Everything gets put in to the "transaction" object. You can think of this as the opposite of the "transactionPayload" computed property
     * @param savedData
     * @returns {Promise<void>}
     */
    async restoreSavedData(savedData) {
      this.jobDetails = await restoreCompanyJob(savedData)
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  margin-bottom: 20px;
}

.content-milestones-and-route .column {
  height: 550px;
  overflow: auto;
}

.job-point {
  border-bottom: 1px solid #e4e4e4;
  margin: 0 0 15px 0;
  padding: 0 0 15px 0;
}

.details-page-wrapper {
  min-height: 85vh;
  &::after {
    // TODO: this would be great as a mix-in
    content: '';
    background-image: url('../../assets/backgrounds/shutterstock_693905746.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    opacity: 0.4;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: fixed;
    z-index: -1;
  }
}
</style>
